// client/src/pages/UsuariosPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getUsuarios, register as createUsuario, updateUsuario, deleteUsuario } from "../api/usuarios.js";
import { getRestaurantes } from "../api/restaurantes.js"; 
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const UsuariosPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [usuarios, setUsuarios] = useState([]);
    const [usuario, setUsuario] = useState({ id_restaurante: "", nombre: "", cedula: "", direccion: "", telefono: "", rol: "", correo: "", contrasenia: "",estado: "activo", });
    const [filteredUsuarios, setFilteredUsuarios] = useState([]);
    const [searchTerm, setSearchTerm] = useState(""); 
    const inputRefs = useRef({}); // aquí guardaremos los refs dinámicamente 
    // Hook de ordenamiento reutilizable
    const { sortedItems: sortedUsuarios, sortConfig, setSortConfig } = useSortableData(filteredUsuarios);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentUsuarios, totalPages, itemsPerPage } = usePagination(sortedUsuarios, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [restaurantes, setRestaurantes] = useState([]);

    // Cargar todos los restaurantes
    useEffect(() => {
    const loadRestaurantes = async () => {
        
        try {
        const data = await getRestaurantes();
        // ✅ Aseguramos que sea un array
        const list = Array.isArray(data) ? data : data.restaurantes || [];
        setRestaurantes(list);
        } catch (err) {
        console.error("Error al cargar restaurantes:", err);
        }
    };
    loadRestaurantes();
    }, []);

    // Cargar todos los usuarios
    const loadUsuarios = async () => {
    try {
        const data = await getUsuarios();
        console.log(" Datos recibidos:", data); 
        const list = Array.isArray(data) ? data : data.usuarios || []; // ✅ Ajuste aquí
        setUsuarios(list);
        setFilteredUsuarios(list);
    } catch (err) {
        console.error("Error al cargar usuarios:", err);
    }
    };
    
    useEffect(() => {
        loadUsuarios();
    }, []);

    // Filtro general
    useEffect(() => {
    const filtered = usuarios.filter((u) =>
        Object.values(u).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    setFilteredUsuarios(filtered);
    }, [searchTerm, usuarios]);

    // Manejar cambios en inputs
    const handleChange = (e) => {
        setUsuario({ ...usuario, [e.target.name]: e.target.value });
    };

    // Resetear formulario
    const resetForm = () => {
        setUsuario({ 
            id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "",
            nombre: "", 
            cedula: "", 
            direccion: "", 
            telefono: "", 
            rol: "", 
            correo: "", 
            contrasenia: "", 
            estado: "activo",
        });
        setErrors({});
        setEditMode(false);
    };

    // --- VALIDACIÓN ---
    const validateUsuario = (data) => {
        const newErrors = {};
        if (!data.nombre.trim()) newErrors.nombre = "El nombre es obligatorio.";
        if (!data.cedula.trim()) newErrors.cedula = "La cédula es obligatoria.";
        if (!data.direccion.trim()) newErrors.direccion = "La dirección es obligatoria.";
        else if (!/^\d{10}$/.test(data.cedula)) newErrors.cedula = "Debe tener 10 dígitos.";
        if (!data.telefono.trim()) newErrors.telefono = "El Teléfono es obligatoria.";
        else if (!/^\d{10}$/.test(data.telefono)) newErrors.telefono = "Debe tener 10 dígitos.";
        if (!data.correo.trim()) { newErrors.correo = "El correo es obligatorio."; } 
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo.trim())) { newErrors.correo = "El formato del correo no es válido."; }
        if (!data.rol) newErrors.rol = "Seleccione un rol.";
        if (!editMode && !data.contrasenia.trim()) newErrors.contrasenia = "Ingrese una contraseña.";
        if (!data.id_restaurante) newErrors.id_restaurante = "Seleccione un restaurante.";
        return newErrors;
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateUsuario(usuario);

        if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        // Scroll al primer campo con error
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldRef = inputRefs.current[firstErrorField];
        if (fieldRef && fieldRef.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus(); // opcional: enfoca el input
        }

        return;
        }

        try {
        if (editMode) {
            await updateUsuario(usuario.id_usuario, usuario);
            alert("Usuario actualizado correctamente");
        } else {
            const { estado, fecha_registro, ...usuarioSinExtras } = usuario;
            
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante del usuario logueado
            if (!usuarioLogueado?.isSuperAdmin) {
                usuarioSinExtras.id_restaurante = usuarioLogueado?.id_restaurante;
            }
            
            await createUsuario(usuarioSinExtras);
            alert("Usuario creado correctamente");
        }
        resetForm();
        setModalOpen(false);
        loadUsuarios();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            setErrors({ general: msg });
            alert(msg);
        }
    };

    // --- EDITAR ---
    const handleEdit = (u) => {
        setUsuario(u);
        setEditMode(true);
        setModalOpen(true);
    };

    // --- ELIMINAR ---
    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar este usuario?")) {
        await deleteUsuario(id);
        loadUsuarios();
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center"> Gestión de Usuarios</h1>
        {/* Buscador + Botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar por cualquier campo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nuevo Usuario </button>
        </div>

        {/* Tabla */}
            <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
                <thead className="bg-gray-100 text-gray-700 font-semibold">
                    <tr className="cursor-pointer">
                        <SortableHeader label="N" sortKey="id_usuario" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <SortableHeader label="Nombres" sortKey="nombre" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <SortableHeader label="Cédula" sortKey="cedula" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <SortableHeader label="Teléfono" sortKey="telefono" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <SortableHeader label="Correo" sortKey="correo" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        {usuarioLogueado?.isSuperAdmin && (
                            <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        )}
                        <SortableHeader label="Rol" sortKey="rol" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                        <th className="px-4 py-2 text-center">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                {currentUsuarios.length > 0 ? (
                    currentUsuarios.map((u, idx) => (
                    <tr key={u.id_usuario} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                        <td className="px-4 py-2">{u.nombre}</td>
                        <td className="px-4 py-2">{u.cedula}</td>
                        <td className="px-4 py-2">{u.telefono}</td>
                        <td className="px-4 py-2">{u.correo}</td>
                        {usuarioLogueado?.isSuperAdmin && (
                            <td className="px-4 py-2">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                    {u.restaurante?.nombre_comercial || "Sin restaurante"}
                                </span>
                            </td>
                        )}
                        <td className="px-4 py-2">{u.rol}</td>
                        <td className={`px-4 py-2 text-center font-semibold ${u.estado === "activo" ? "text-green-600" : "text-red-500"}`}>
                            {u.estado}
                        </td>
                        <td className="px-4 py-2 text-center flex justify-center gap-2">
                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(u)}>Editar</button>
                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(u.id_usuario)}>Eliminar</button>
                        </td>
                    </tr>
                    ))
                ) : (
                    <tr>
                    <td colSpan={usuarioLogueado?.isSuperAdmin ? "9" : "8"} className="text-center py-4 text-gray-500">
                        No hay usuarios registrados
                    </td>
                    </tr>
                )}
                </tbody>
            </table>
            </div>

            {/* Paginación */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* Modal de Crear / Editar Usuario */}
            {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">

                {/* Botón cerrar */}
                <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl" > ✖ </button>

                {/* Título */}
                <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2"> {editMode ? "Editar Usuario" : "Nuevo Usuario"} </h2>
                {/* Formulario principal */}
                <form onSubmit={handleSubmit} className="space-y-6">

                    {/* Datos Personales */}
                    <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Datos Personales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputGroup label="Nombres" error={errors.nombre}> <input name="nombre" value={usuario.nombre} onChange={handleChange} placeholder="Ingrese nombre completo" className={`input-base ${ errors.nombre ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["nombre"] = el)} /> </InputGroup>
                        <InputGroup label="Cédula" error={errors.cedula}> <input name="cedula" value={usuario.cedula} onChange={handleChange} placeholder="Ingrese número de cédula" className={`input-base ${ errors.cedula ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["cedula"] = el)} /> </InputGroup>
                        <InputGroup label="Dirección" error={errors.direccion}> <input name="direccion" value={usuario.direccion} onChange={handleChange} placeholder="Ingrese dirección" className={`input-base ${ errors.direccion ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["direccion"] = el)} /> </InputGroup>
                        <InputGroup label="Teléfono" error={errors.telefono}> <input name="telefono" value={usuario.telefono} onChange={handleChange} placeholder="Ingrese teléfono" className={`input-base ${ errors.telefono ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["telefono"] = el)} /> </InputGroup>
                    </div>
                    </section>

                    {/* Información de Cuenta */}
                    <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Información de Cuenta</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Campo Restaurante - Solo visible para SuperAdmin */}
                        {usuarioLogueado?.isSuperAdmin && (
                            <InputGroup label="Restaurante" error={errors.id_restaurante}>
                            <select
                                name="id_restaurante"
                                value={usuario.id_restaurante}
                                onChange={handleChange}
                                className={`input-base ${errors.id_restaurante ? "border-red-500 focus:ring-red-400" : ""}`}
                                ref={(el) => (inputRefs.current["id_restaurante"] = el)}
                            >
                                <option value="">Seleccione un restaurante</option>
                                {restaurantes.map((r) => (
                                <option key={r.id_restaurante} value={r.id_restaurante}> {r.nombre_comercial} </option>
                                ))}
                            </select>
                            </InputGroup>
                        )}

                        <InputGroup label="Rol" error={errors.rol}>
                        <select name="rol" value={usuario.rol} onChange={handleChange} className={`input-base ${ errors.rol ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["rol"] = el)} >
                            <option value="">Seleccione un rol</option>
                            <option value="admin">Administrador</option>
                            <option value="mesero">Mesero</option>
                        </select>
                        </InputGroup>
                        <InputGroup label="Correo" error={errors.correo}> <input name="correo" value={usuario.correo} onChange={handleChange} placeholder="pepitoperez@gmail.com" className={`input-base ${errors.correo ? "border-red-500 focus:ring-red-400" : ""}`} ref={(el) => (inputRefs.current["correo"] = el)} /> </InputGroup>
                        {!editMode && (
                        <InputGroup label="Contraseña" error={errors.contrasenia}> <input type="password" name="contrasenia" value={usuario.contrasenia} onChange={handleChange} placeholder="Ingrese contraseña" className={`input-base ${ errors.contrasenia ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["contrasenia"] = el)} /> </InputGroup>
                        )}
                        {/* Fecha de registro — solo visible al editar */}
                        {editMode && (
                        <InputGroup label="Fecha de Registro" error={errors.fecha_registro}> <input type="date" name="fecha_registro" value={usuario.fecha_registro?.split("T")[0] || ""} onChange={handleChange} className={`input-base ${ errors.fecha_registro ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["fecha_registro"] = el)} /> </InputGroup>
                        )}
                        {/* Estado — solo visible al editar */}
                        {editMode && (
                        <InputGroup label="Estado" error={errors.estado}> <select name="estado" value={usuario.estado || "activo"} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["estado"] = el)} > <option value="activo">Activo</option> <option value="inactivo">Inactivo</option> </select> </InputGroup>
                        )}
                    </div>
                    </section>

                    {/* Botones de acción */}
                    <div className="flex justify-end gap-4 mt-6">
                    <button type="button" onClick={() => setModalOpen(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded" > Cancelar </button>
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded" > {editMode ? "Actualizar" : "Guardar"} </button>
                    </div>
                </form>
                </div>
            </div>
            )}

        </div>
    );
};
export default UsuariosPage;
